/**
 * P06 B5 – đánh giá duyệt giá báo giá (docs/phases/P06.md §5 mục 7, §8; ADR-014).
 * Hàm thuần: quy tắc là dữ liệu (approval_rules), service nạp quy tắc + dữ kiện rồi gọi hàm này.
 * - DEC-003 A: CK hiệu dụng > 5% hoặc lãi gộp < 5% → Trưởng phòng KD; tổng > 500 triệu hoặc lãi gộp < 0 → Giám đốc.
 * - Đánh giá ở cấp báo giá: CK hiệu dụng lớn nhất trong các dòng, lãi gộp cả phiếu, tổng chưa VAT; lấy cấp cao nhất.
 * - DEC-042 + DEC-137 A: tổng chưa VAT (gồm vận chuyển) ≤ 9 triệu → người lập tự duyệt.
 * - DEC-119: người lập có vai trò của quy tắc creator_role (CTV) → luôn cần duyệt, không xét ngưỡng khác, không tự duyệt.
 */
import { formatMoney, formatQty } from './format';

export const APPROVAL_STATUSES = ['none', 'pending', 'approved', 'rejected'] as const;
export type ApprovalStatus = (typeof APPROVAL_STATUSES)[number];

export const APPROVAL_STATUS_LABELS: Record<ApprovalStatus, string> = {
  none: 'Chưa gửi duyệt',
  pending: 'Chờ duyệt',
  approved: 'Đã duyệt',
  rejected: 'Bị từ chối',
};

export type ApprovalRuleType = 'discount' | 'margin' | 'amount' | 'creator_role' | 'self_approve';
/** department = TP cùng phòng NV phụ trách; all = mọi báo giá; manager = NV kinh doanh quản lý CTV lập báo giá (DEC-300) */
export type ApprovalScope = 'department' | 'all' | 'manager';

/** Tên người duyệt hiển thị cho quy tắc NV quản lý CTV (vai trò "Nhân viên kinh doanh" dễ hiểu nhầm là mọi NV) */
export const CTV_MANAGER_APPROVER_NAME = 'NV kinh doanh quản lý CTV';

/**
 * DEC-300 (anh Tiến 13/10/2026): "Trưởng phòng duyệt, để trạng thái 'TP đã duyệt' cho giám đốc thấy".
 * Nhãn trạng thái đã duyệt theo vai trò người duyệt (approval_requests.decided_role).
 */
export const APPROVED_BY_ROLE_LABELS: Record<string, string> = {
  sales_manager: 'TP đã duyệt',
  director: 'GĐ đã duyệt',
  sales: 'NV quản lý đã duyệt',
  sales_admin: 'Sales admin đã duyệt',
};
/** DEC-300: tên ngắn người ghi phương án xử lý theo vai trò đã duyệt */
export const PLANNER_LABELS: Record<string, string> = { director: 'Giám đốc', sales_manager: 'Trưởng phòng', sales: 'NV quản lý CTV', sales_admin: 'Sales admin' };
export const plannerLabel = (role: string | null | undefined) => (role && PLANNER_LABELS[role]) || 'Người duyệt';
export const approvedLabel = (role: string | null | undefined) => (role && APPROVED_BY_ROLE_LABELS[role]) || 'Đã duyệt';

export interface ApprovalRuleDef {
  id: number;
  ruleType: ApprovalRuleType;
  name: string;
  threshold: number;
  level: number;
  scope: ApprovalScope;
  approverRoleCode: string | null;
  approverRoleName: string | null;
  creatorRoleCode: string | null;
}

export interface ApprovalFacts {
  /** CK hiệu dụng lớn nhất trong các dòng (%), null = chưa dòng nào có giá chuẩn */
  maxEffectiveDiscountPercent: number | null;
  /** Lãi gộp % cả phiếu trên các dòng có giá vốn; null = không dòng nào có giá vốn */
  marginPercent: number | null;
  /** Tổng chưa VAT, gồm dòng vận chuyển (DEC-137 A) */
  netTotal: number;
  linesMissingCost: number;
  lineCount: number;
  /** Vai trò của người lập báo giá */
  creatorRoles: readonly string[];
  /** DEC-300 bổ sung: mức tự quyết riêng của người lập (thay ngưỡng duyệt cấp phòng); null = theo quy tắc chung */
  selfApproveMax?: number | null;
  /** DEC-300 bổ sung: mức duyệt riêng cao nhất của Trưởng phòng phòng NV (thay ngưỡng lên Giám đốc); null = theo quy tắc chung */
  managerApproveMax?: number | null;
}

export interface ApprovalReason {
  ruleId: number;
  ruleType: ApprovalRuleType;
  level: number;
  /** Dữ kiện so với ngưỡng; null khi người xem không được thấy (lãi gộp cần price.view_cost) */
  fact: number | null;
  threshold: number;
  label: string;
}

export interface ApprovalEvaluation {
  /** Cấp cần duyệt cao nhất; 0 = không cần duyệt (hoặc tự duyệt) */
  level: number;
  required: boolean;
  reasons: ApprovalReason[];
  /** Vượt ngưỡng nhưng tổng ≤ mốc tự duyệt → người lập tự duyệt (ghi audit) */
  selfApprove: boolean;
  selfApproveMax: number | null;
  /** Vai trò người lập khớp quy tắc creator_role (CTV) */
  creatorRoleCode: string | null;
  /** Tên vai trò được duyệt ở cấp cần duyệt, vd ["Trưởng phòng kinh doanh", "Giám đốc"] */
  approverRoleNames: string[];
}

const pct = (v: number) => `${formatQty(v)}%`;
const vnd = (v: number) => `${formatMoney(v)} đ`;

function reasonLabel(r: ApprovalRuleDef, fact: number | null): string {
  switch (r.ruleType) {
    case 'discount':
      return `CK ${fact === null ? '' : `${pct(fact)} `}> ${pct(r.threshold)}`;
    case 'margin':
      return r.threshold === 0 ? 'Bán lỗ (lãi gộp < 0%)' : `Lãi gộp ${fact === null ? '' : `${pct(fact)} `}< ${pct(r.threshold)}`;
    case 'amount':
      return `Tổng chưa VAT ${fact === null ? '' : `${vnd(fact)} `}> ${vnd(r.threshold)}`;
    case 'creator_role':
      return 'Báo giá do CTV lập luôn cần duyệt';
    default:
      return r.name;
  }
}

/**
 * DEC-300 bổ sung (anh Tiến 13/10/2026: "điều chỉnh mềm"): ngưỡng tổng tiền thực tế của một quy tắc amount.
 *  - Quy tắc cấp phòng (Trưởng phòng duyệt): mức tự quyết riêng của người lập thay ngưỡng chung (27 tr).
 *  - Quy tắc toàn công ty (Giám đốc duyệt): mức duyệt riêng của Trưởng phòng thay ngưỡng chung (52 tr);
 *    không thấp hơn mức tự quyết của người lập (NV tự quyết 60 tr thì 55 tr không lên ai).
 */
export function amountThreshold(r: Pick<ApprovalRuleDef, 'scope' | 'threshold'>, f: Pick<ApprovalFacts, 'selfApproveMax' | 'managerApproveMax'>): number {
  const self = f.selfApproveMax ?? null;
  if (r.scope === 'department') return self ?? r.threshold;
  return Math.max(f.managerApproveMax ?? r.threshold, self ?? 0);
}

/** Ngưỡng chung lên cấp trên Trưởng phòng (52 tr): mức duyệt mặc định của một Trưởng phòng chưa có mức riêng. */
export function managerDefaultLimit(rules: readonly ApprovalRuleDef[]): number | null {
  const dept = rules.filter((r) => r.ruleType === 'amount' && r.scope === 'department');
  if (!dept.length) return null;
  const lvl = Math.min(...dept.map((r) => r.level));
  const up = rules.filter((r) => r.ruleType === 'amount' && r.scope !== 'department' && r.level > lvl).map((r) => r.threshold);
  return up.length ? Math.min(...up) : null;
}

/** Quy tắc áp cho người lập: có quy tắc creator_role khớp vai trò thì chỉ dùng các quy tắc đó (DEC-119). */
export function applicableRules(rules: readonly ApprovalRuleDef[], creatorRoles: readonly string[]) {
  const creatorRule = rules.find((r) => r.ruleType === 'creator_role' && r.creatorRoleCode && creatorRoles.includes(r.creatorRoleCode));
  const creatorRoleCode = creatorRule?.creatorRoleCode ?? null;
  const list = creatorRoleCode
    ? rules.filter((r) => r.ruleType === 'creator_role' && r.creatorRoleCode === creatorRoleCode)
    : rules.filter((r) => r.ruleType !== 'creator_role');
  return { creatorRoleCode, rules: list };
}

export function evaluateApproval(rules: readonly ApprovalRuleDef[], f: ApprovalFacts): ApprovalEvaluation {
  const { creatorRoleCode, rules: list } = applicableRules(rules, f.creatorRoles);
  const reasons: ApprovalReason[] = [];
  for (const r of list) {
    let fact: number | null = null;
    let hit = false;
    let threshold = r.threshold;
    switch (r.ruleType) {
      case 'discount':
        fact = f.maxEffectiveDiscountPercent;
        hit = fact !== null && fact > r.threshold;
        break;
      case 'margin':
        fact = f.marginPercent;
        hit = fact !== null && fact < r.threshold;
        break;
      case 'amount':
        fact = f.netTotal;
        threshold = amountThreshold(r, f);
        hit = fact > threshold;
        break;
      case 'creator_role':
        hit = f.lineCount > 0;
        break;
      default:
        break;
    }
    if (hit) reasons.push({ ruleId: r.id, ruleType: r.ruleType, level: r.level, fact, threshold, label: reasonLabel({ ...r, threshold }, fact) });
  }
  // Nhiều quy tắc cùng loại (vd lãi gộp < 5% và < 0): chỉ giữ quy tắc cấp cao nhất của loại đó
  const top = new Map<ApprovalRuleType, ApprovalReason>();
  for (const x of reasons) if ((top.get(x.ruleType)?.level ?? -1) < x.level) top.set(x.ruleType, x);
  const kept = [...top.values()].sort((a, b) => b.level - a.level || a.ruleId - b.ruleId);
  const level = kept.reduce((m, x) => Math.max(m, x.level), 0);

  const selfRule = creatorRoleCode ? undefined : rules.find((r) => r.ruleType === 'self_approve');
  const selfApprove = level > 0 && !!selfRule && f.netTotal <= selfRule.threshold;
  const approverRoleNames = [
    ...new Set(
      list
        .filter((r) => r.ruleType !== 'self_approve' && r.level === level && r.approverRoleName)
        // NV quản lý CTV, rồi người duyệt theo phòng (Trưởng phòng) đứng trước người duyệt toàn công ty
        .sort((a, b) => Number(a.scope !== 'manager') - Number(b.scope !== 'manager') || Number(a.scope === 'all') - Number(b.scope === 'all') || a.id - b.id)
        .map((r) => (r.scope === 'manager' ? CTV_MANAGER_APPROVER_NAME : r.approverRoleName!)),
    ),
  ];
  return {
    level: selfApprove ? 0 : level,
    required: level > 0 && !selfApprove,
    reasons: kept,
    selfApprove,
    selfApproveMax: selfRule ? selfRule.threshold : null,
    creatorRoleCode,
    approverRoleNames,
  };
}

/**
 * Người giữ vai trò nào, ở phạm vi nào được duyệt một yêu cầu cấp `level`:
 * các quy tắc áp cho người lập (kể cả quy tắc chung, để Giám đốc duyệt được mọi báo giá – DEC-042) có cấp ≥ level.
 */
export function approverRulesFor(rules: readonly ApprovalRuleDef[], level: number, creatorRoleCode: string | null) {
  return rules.filter(
    (r) =>
      r.ruleType !== 'self_approve' &&
      r.approverRoleCode &&
      r.level >= level &&
      (r.ruleType !== 'creator_role' || (creatorRoleCode !== null && r.creatorRoleCode === creatorRoleCode)),
  );
}

/** Ngưỡng CK hiệu dụng thấp nhất đang áp (tô đỏ ô CK trên lưới báo giá). */
export function discountLimit(rules: readonly ApprovalRuleDef[]): number | null {
  const t = rules.filter((r) => r.ruleType === 'discount').map((r) => r.threshold);
  return t.length ? Math.min(...t) : null;
}
